import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const SurveyPage = () => {
  const { slug } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notLive, setNotLive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchSurvey = async () => {
      setLoading(true);
      setNotFound(false);
      setNotLive(false);
      try {
        const url = slug ? `/api/surveys/public/${slug}` : '/api/surveys/active';
        const response = await axios.get(url);
        setSurvey(response.data.survey);
        setAnswers({});
        setName('');
        setEmail('');
        setSubmitted(false);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          if (slug) {
            setNotFound(true);
          } else {
            setSurvey(null);
          }
        } else if (error.response && error.response.status === 403) {
          setNotLive(true);
        } else {
          toast.error(error.response?.data?.message || 'Could not load the survey');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [slug]);

  const handleSingle = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleMulti = (questionId, value) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [questionId]: next };
    });
  };

  const handleRating = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        responder: { name: name.trim(), email: email.trim() },
        answers: survey.questions
          .map((q) => ({ questionId: q.id, value: answers[q.id] }))
          .filter((a) => a.value !== undefined && a.value !== null && !(Array.isArray(a.value) && a.value.length === 0))
      };
      await axios.post(`/api/surveys/${survey._id}/submit`, payload);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (notLive) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-xl shadow-lg p-8 max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-500/10 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-primary">Survey Not Live</h1>
          <p className="text-secondary">
            This survey is not accepting responses right now. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-xl shadow-lg p-8 max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-bold text-primary">Survey Not Found</h1>
          <p className="text-secondary">
            We could not find that survey. The link may be incorrect or the survey has been removed.
          </p>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-xl shadow-lg p-8 max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-bold text-primary">No Active Survey</h1>
          <p className="text-secondary">
            There is no survey open right now. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-xl shadow-lg p-8 max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-500/10 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-primary">Thank You!</h1>
          <p className="text-secondary">
            Your responses have been recorded. We appreciate your feedback.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Branded header */}
        <div className="flex items-center justify-center mb-8">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-card rounded-xl shadow-lg overflow-hidden">
          {/* Survey header */}
          <div className="px-6 py-6 sm:px-8 border-b border-primary">
            <h1 className="text-2xl font-bold text-primary">{survey.title}</h1>
            {survey.description && (
              <p className="mt-2 text-secondary">{survey.description}</p>
            )}
          </div>

          <div className="px-6 py-6 sm:px-8 space-y-8">
            {survey.questions.map((question, index) => (
              <div key={question.id}>
                <label className="block text-sm font-medium text-primary mb-3">
                  <span className="text-muted mr-2">{index + 1}.</span>
                  {question.label}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {question.type === 'text' && (
                  <textarea
                    rows={3}
                    value={answers[question.id] || ''}
                    onChange={(e) => handleSingle(question.id, e.target.value)}
                    placeholder="Your answer..."
                    className="form-input w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}

                {question.type === 'single' && (
                  <div className="space-y-2">
                    {question.options.map((option) => (
                      <label
                        key={option}
                        className="flex items-center p-3 border border-primary rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option}
                          checked={answers[question.id] === option}
                          onChange={() => handleSingle(question.id, option)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-3 text-sm text-secondary">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'multi' && (
                  <div className="space-y-2">
                    {question.options.map((option) => (
                      <label
                        key={option}
                        className="flex items-center p-3 border border-primary rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <input
                          type="checkbox"
                          value={option}
                          checked={Array.isArray(answers[question.id]) && answers[question.id].includes(option)}
                          onChange={() => handleMulti(question.id, option)}
                          className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <span className="ml-3 text-sm text-secondary">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'rating' && (
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        type="button"
                        key={num}
                        onClick={() => handleRating(question.id, num)}
                        className={`w-12 h-12 rounded-lg border text-lg font-bold transition-colors ${
                          answers[question.id] === num
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-card text-secondary border-secondary hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Optional responder info */}
            <div className="border-t border-primary pt-6 space-y-4">
              <p className="text-sm text-tertiary">
                Optional: leave your details and we can follow up on your feedback.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-input w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 sm:px-8 border-t border-primary bg-tertiary flex items-center justify-between">
            <p className="text-xs text-muted">
              {survey.questions.filter((q) => q.required).length} required {survey.questions.filter((q) => q.required).length === 1 ? 'question' : 'questions'}
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyPage;